"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * T2 reorder flow — two screens that replace the abrupt "Al Madar drafts a
 * new proposal" beat with a realistic negotiation:
 *
 *   4.2 ReorderDraft           — Al Madar reuses T1 spec, only quantity editable
 *   4.3 CounterOfferAndAccept  — inventory agent: 80 → 50 t counter
 *                                (animated qty transition), accepted at 50 t.
 *
 * Collapses the previous 4.3 AuroraCounterOffer + 4.4 ProposalAdjusted
 * into one screen with the qty value tweening 80 → 50 over ~1.5 s on
 * mount. The audience sees the negotiation as one motion, not two
 * static snapshots.
 */

const T1_SPEC = {
  commodity:    "Frozen Halal Whole Chicken",
  hsCode:       "0207.12",
  incoterms:    "CIF",
  origin:       "Santos · BR",
  destination:  "Jebel Ali · UAE",
  unitPriceUsd: 30,            // ~ USD 850k / 28 t in T1
  cutTempC:     -18,
};

const T2_REQUESTED_QTY = 80_000;
const T2_AVAILABLE_QTY = 50_000;

export function ReorderDraft({ className }: { className?: string }) {
  const t        = useTranslations("reorderFlow");
  const priceUsd = T2_REQUESTED_QTY * T1_SPEC.unitPriceUsd;
  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between border-b border-state-verified/15 pb-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("draftKicker")}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("draftTitle")}</h3>
          </div>
          <span className="rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-verified">
            {t("verifiedTraderBadge")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldCommodity")}     value={`${T1_SPEC.commodity} · HS ${T1_SPEC.hsCode}`} locked />
          <Field label={t("fieldQuantity")}      value={`${T2_REQUESTED_QTY.toLocaleString()} kg`} editable />
          <Field label={t("fieldIndicativePrice")} value={`USD ${priceUsd.toLocaleString()} · USD ${T1_SPEC.unitPriceUsd.toFixed(2)}/kg`} locked />
          <Field label={t("fieldFunding")}       value={t("lcBankFinanced")} />
          <Field label={t("fieldOriginDest")}    value={`${T1_SPEC.origin} → ${T1_SPEC.destination}`} locked />
          <Field label={t("fieldIncoterms")}     value={T1_SPEC.incoterms} locked />
          <Field label={t("fieldColdChain")}     value={`${T1_SPEC.cutTempC} °C IQF`} locked />
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-3 py-2 text-[11px]">
          <span className="font-mono text-state-verified">●</span>
          <span className="text-orbitra-paper">
            {t("lastTradeNote")}
          </span>
          <button type="button" onClick={advanceScreen} className="ms-auto cta-primary px-3 py-1 text-[11px]">{t("submitReorderCta")}</button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("echoAgentDraftLabel")}</p>
          <p className="mt-2 text-orbitra-paper">"{t("echoAgentDraftQuote")}"</p>
        </div>
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("reuseKicker")}</p>
          <p className="mt-2 text-[11px] text-orbitra-paper">{t("reuseBody")}</p>
          <p className="mt-1 text-[10px] text-orbitra-muted">{t("reuseTrustNote")}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Counter-offer + accept on a single screen. Mounts at 80 t and tweens
 * the visible qty value down to 50 t over ~1.6 s. The "Accepted ✓" badge
 * flips on once the tween completes (visible state = accepted). Price
 * updates in lock-step with qty (unit price held at USD 30/kg).
 */
export function CounterOfferAndAccept({ className }: { className?: string }) {
  const t = useTranslations("reorderFlow");
  const [qty, setQty] = useState(T2_REQUESTED_QTY); // start at 80,000
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    // Tween 80,000 → 50,000 over 1.6s using requestAnimationFrame.
    const start = performance.now();
    const DURATION = 1600;
    const from = T2_REQUESTED_QTY;
    const to   = T2_AVAILABLE_QTY;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setQty(value);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setAccepted(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const priceUsd = qty * T1_SPEC.unitPriceUsd;

  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between border-b pb-3 transition-colors duration-300"
             style={{ borderColor: accepted ? "rgba(45,143,78,0.30)" : "rgba(124,108,216,0.15)" }}>
          <div>
            <p className={clsx(
              "font-mono text-[9px] uppercase tracking-[0.18em] transition-colors",
              accepted ? "text-policy-pass" : "text-orbitra-teal-soft",
            )}>
              {accepted ? t("counterKickerAccepted") : t("counterKickerPending")}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {accepted ? t("counterTitleAccepted") : t("counterTitlePending")}
            </h3>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors duration-500",
            accepted
              ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
              : "border-state-financed/40 bg-state-financed/10 text-state-financed",
          )}>
            {accepted ? t("counterBadgeAccepted") : t("counterBadgePending")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldCommodity")}    value={`${T1_SPEC.commodity} · HS ${T1_SPEC.hsCode}`} locked />
          <Field
            label={t("fieldQuantityLive")}
            value={`${qty.toLocaleString()} kg`}
            tone={accepted ? "ok" : "warn"}
          />
          <Field label={t("fieldFunding")}      value={t("lcBankFinanced")} />
          <Field label={t("fieldOriginDest")}   value={`${T1_SPEC.origin} → ${T1_SPEC.destination}`} locked />
          <Field
            label={t("fieldPricePerKg")}
            value={`USD ${priceUsd.toLocaleString()}`}
            tone={accepted ? "ok" : undefined}
          />
          <Field label={t("fieldIncoterms")}    value={T1_SPEC.incoterms} locked />
          <Field label={t("fieldContractRef")}  value="ORB-CTR-2026-0187" locked mono />
        </div>

        <div className="mt-4 rounded-md border border-state-financed/30 bg-state-financed/8 p-3 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("counterInventoryAgentLabel")}</p>
          <p className="mt-1 text-orbitra-paper italic">
            "{t("inventoryAgentQuote")}"
          </p>
        </div>

        <div className={clsx(
          "mt-3 flex items-center gap-3 rounded-md border px-3 py-2 text-[11px] transition-colors duration-500",
          accepted
            ? "border-policy-pass/30 bg-policy-pass/8 text-orbitra-paper"
            : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted",
        )}>
          <span className={clsx("font-mono", accepted ? "text-policy-pass" : "text-orbitra-muted")}>●</span>
          <span>{accepted ? t("negotiationClosed") : t("alMadarReviewing")}</span>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("counterEchoAgentLabel")}</p>
          <p className="mt-2 text-orbitra-paper">"{t("echoAgentCounterQuote")}"</p>
        </div>
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("whatChangedKicker")}</p>
          <ul className="mt-2 list-disc ps-4 text-[11px] text-orbitra-paper">
            <li>{t("changeQty")} <span className="text-state-financed">50 t</span></li>
            <li>{t("changePrice")} <span className="text-state-financed">USD 1,500,000</span></li>
            <li>{t("changeUnitPriceHeld")}</li>
            <li>{t("changeShipDate")}</li>
          </ul>
          <p className="mt-3 text-[10px] text-orbitra-muted">
            {t("brokerNote")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  locked = false,
  editable = false,
  tone,
  mono = false,
}: {
  label: string;
  value: string;
  locked?: boolean;
  editable?: boolean;
  tone?: "ok" | "warn";
  mono?: boolean;
}) {
  return (
    <div
      className={clsx(
        "rounded-md border bg-orbitra-ink/40 px-3 py-2",
        tone === "ok"   ? "border-policy-pass/40" :
        tone === "warn" ? "border-state-financed/40" :
                          "border-state-verified/20",
      )}
    >
      <p className="flex items-center gap-2 text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">
        {label}
        {locked   && <span className="text-[8px] normal-case tracking-normal text-orbitra-muted/70">locked</span>}
        {editable && <span className="text-[8px] normal-case tracking-normal text-state-verified">editable</span>}
      </p>
      <p className={clsx("mt-0.5 text-orbitra-paper", mono && "font-mono")}>{value}</p>
    </div>
  );
}
